/**
 * Central route registry — single source of truth for page URLs in all 3 languages.
 *
 * URL strategy
 * ─────────────
 *   ET (default, no prefix):   /teenused, /hinnakiri, /arstid, /kontakt, /haigused, /kalkulaatorid
 *   RU (Cyrillic slugs):        /ru/услуги, /ru/цены, /ru/врачи, /ru/контакты, /ru/заболевания, /ru/калькуляторы
 *   EN:                         /en/services, /en/pricing, /en/doctors, /en/contact, /en/diseases, /en/calculators
 *
 * Rationale (per user choice — 2026-07):
 *   Cyrillic slugs improve UX and organic CTR for Russian speakers.
 *   React Router handles unencoded Cyrillic in paths natively; browsers show
 *   them decoded in the URL bar but transmit as %-encoded UTF-8.
 *
 * Backwards compatibility
 * ────────────────────────
 * The old Estonian-slug URLs in /ru and /en (e.g. `/ru/teenused`) are still
 * live routes but respond with a 301-style <Navigate replace> to the new
 * canonical, so any Google Search Console-indexed URL keeps working and
 * ranking signals consolidate on the canonical.
 */

// Ordered page keys — used to build routes, sitemap, redirects.
export const PAGE_KEYS = ["home", "services", "pricing", "doctors", "contact", "diseases", "calculators"];

// Per-language URL segment for each page. Home has no segment ("").
export const PAGE_SLUGS = {
  home: { et: "", ru: "", en: "" },
  services: { et: "teenused", ru: "услуги", en: "services" },
  pricing: { et: "hinnakiri", ru: "цены", en: "pricing" },
  doctors: { et: "arstid", ru: "врачи", en: "doctors" },
  contact: { et: "kontakt", ru: "контакты", en: "contact" },
  diseases: { et: "haigused", ru: "заболевания", en: "diseases" },
  calculators: { et: "kalkulaatorid", ru: "калькуляторы", en: "calculators" },
};

// Old (pre-2026-07) slugs that were Estonian for all languages. Used to
// generate 301 redirect routes so /ru/teenused → /ru/услуги etc.
export const LEGACY_PAGE_SLUGS = {
  services: "teenused",
  pricing: "hinnakiri",
  doctors: "arstid",
  contact: "kontakt",
  diseases: "haigused",
  calculators: "kalkulaatorid",
};

/**
 * Build a language-prefixed URL for a given page key.
 *   pagePath("services", "et") -> "/teenused"
 *   pagePath("services", "ru") -> "/ru/услуги"
 *   pagePath("services", "en") -> "/en/services"
 *   pagePath("home",     "ru") -> "/ru"
 */
export function pagePath(page, lang) {
  const prefix = lang === "et" ? "" : `/${lang}`;
  const slug = PAGE_SLUGS[page]?.[lang];
  if (slug === undefined) return "/";
  if (slug === "") return prefix || "/";
  return `${prefix}/${slug}`;
}

/**
 * The URL segment (without leading slash) for a page in a given language.
 * Useful when building React Router <Route path="..."> definitions.
 */
export function pageSlug(page, lang) {
  return PAGE_SLUGS[page]?.[lang];
}

/**
 * Reverse: given any known slug (in any language, including the legacy
 * Estonian slug when used in RU/EN paths), return the page key.
 * Falls back to null for unknown slugs.
 */
export function pageKeyFromSlug(slug) {
  if (!slug) return "home";
  for (const key of PAGE_KEYS) {
    const s = PAGE_SLUGS[key];
    if (s && (s.et === slug || s.ru === slug || s.en === slug)) return key;
  }
  return null;
}

/**
 * Translate a URL from one language to another, preserving the page and
 * any sub-path (e.g. a disease article slug or pricing section). Used
 * by the language switcher in the Header.
 *
 *   translatePath("/teenused", "ru")            -> "/ru/услуги"
 *   translatePath("/ru/услуги", "en")           -> "/en/services"
 *   translatePath("/haigused/1-tuupi-diabeet","en")
 *                                                -> "/en/diseases/type-1-diabetes"
 *   translatePath("/", "ru")                    -> "/ru"
 *
 * Sub-slugs (article slugs, pricing section slugs) are translated by
 * consulting `pricingSections.js` and `diseaseSlugs.js` via callbacks
 * (imported lazily to avoid circular deps).
 */
export function translatePath(pathname, targetLang, subSlugTranslator) {
  // React Router 7 may hand us URL-encoded paths (e.g. Cyrillic slugs come
  // through as %D0%A3%D1%81…). Decode before splitting so pageKeyFromSlug
  // can match Cyrillic slugs.
  let decoded = pathname;
  try {
    decoded = decodeURI(pathname);
  } catch {
    decoded = pathname;
  }

  // Strip current language prefix
  let rest = decoded.replace(/^\/(ru|en)(?=\/|$)/, "");
  if (rest === "") rest = "/";

  // Split into first segment (page), optional sub-slug, and remainder
  const match = rest.match(/^\/([^/]+)(?:\/([^/]+))?(\/.*)?$/);
  const firstSeg = match ? match[1] : "";
  const subSeg = match && match[2] ? match[2] : "";
  const tail = match && match[3] ? match[3] : "";

  // Home
  if (rest === "/" || !firstSeg) {
    return targetLang === "et" ? "/" : `/${targetLang}`;
  }

  // Identify the page from any language's slug (or legacy ET slug in RU/EN URLs)
  const pageKey = pageKeyFromSlug(firstSeg);
  if (!pageKey || pageKey === "home") {
    // Unknown top-level path: just swap prefix and hope for the best
    const prefix = targetLang === "et" ? "" : `/${targetLang}`;
    return `${prefix}${rest}`;
  }

  const targetSlug = PAGE_SLUGS[pageKey][targetLang];
  const prefix = targetLang === "et" ? "" : `/${targetLang}`;

  // Translate sub-slug for pricing / disease pages when a translator is supplied
  let translatedSub = subSeg;
  if (subSeg && subSlugTranslator) {
    translatedSub = subSlugTranslator(pageKey, subSeg, targetLang) || subSeg;
  }
  const subPart = translatedSub ? `/${translatedSub}` : "";
  return `${prefix}/${targetSlug}${subPart}${tail}`;
}

